import { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { sendRequestConfirmation } from '../services/emailService';
import { z, ZodTypeAny } from 'zod';

export interface CarrierFormData {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  dotNumber: string;
  equipmentType: string;
  mcNumber?: string;
  insurancePolicyNumber?: string;
  insuranceProvider?: string;
  insuranceCoverageAmount?: string;
  insuranceExpirationDate?: string;
}

// Sanitización específica para números DOT
function sanitizeDOT(value: string): string {
  // Eliminar todo excepto dígitos
  const cleanValue = value.replace(/[^\d]/g, '');
  // Limitar a 12 dígitos
  return cleanValue.slice(0, 12);
}

// Validación específica para DOT
function validateDOTNumber(value: string): string | null {
  try {
    const cleanValue = sanitizeDOT(value);
    if (cleanValue.length === 0) return 'DOT number is required';
    if (cleanValue.length < 6) return 'DOT number must be at least 6 digits';
    if (cleanValue.length > 12) return 'DOT number cannot exceed 12 digits';
    if (!/^\d+$/.test(cleanValue)) return 'DOT number must contain only digits';
    if (parseInt(cleanValue) === 0) return 'Invalid DOT number';

    return null; // Sin errores
  } catch (error) {
    return 'Invalid DOT number format';
  }
}

const carrierSchema = z.object({
  companyName: z
    .string()
    .min(2, { message: 'Company name too short' }),
  contactName: z
    .string()
    .min(2, { message: 'Contact name too short' }),
  email: z.string().email({ message: 'Invalid email' }),
  phone: z
    .string()
    .regex(/^\d{10,15}$/, { message: 'Invalid phone number' }),
  dotNumber: z
    .string()
    .min(1, { message: 'DOT number is required' })
    .superRefine((value, ctx) => {
      const error = validateDOTNumber(value || '');
      if (error) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: error });
      }
    }),
  equipmentType: z.string().min(1, { message: 'Equipment type is required' }),
  mcNumber: z.string().optional(),
  insurancePolicyNumber: z.string().optional(),
  insuranceProvider: z.string().optional(),
  insuranceCoverageAmount: z.string().optional(),
  insuranceExpirationDate: z.string().optional(),
});

export const useCarrier = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateField = async (name: string, value: string) => {
    try {
      const fieldSchema = carrierSchema.shape[
        name as keyof typeof carrierSchema.shape
      ] as ZodTypeAny;
      const result = fieldSchema.safeParse(value);
      if (result.success) {
        setErrors(prev => ({ ...prev, [name]: '' }));
        return true;
      }
      const issue = result.error.issues[0];
      setErrors(prev => ({
        ...prev,
        [name]: issue?.message ?? 'Invalid value',
      }));
      return false;
    } catch (err) {
      console.error(err);
      setErrors(prev => ({ ...prev, [name]: 'Invalid value' }));
      return false;
    }
  };

  const clearFieldError = (name: string) => {
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const sanitizeFormData = (formData: CarrierFormData): CarrierFormData => {
    return {
      ...formData,
      dotNumber: sanitizeDOT(formData.dotNumber),
    };
  };

  const checkExistingCarrier = async (email: string, dotNumber: string, userId: string) => {
    const { data: existingCarrier } = await supabase
      .from('carrier_registrations')
      .select('id')
      .or(`email.eq.${email},dot_number.eq.${dotNumber}`)
      .neq('user_id', userId)
      .single();

    return existingCarrier;
  };

  const saveCarrier = async (
    carrierData: any,
    isUpdating: boolean,
    recordId: string | null,
    userId: string
  ) => {
    let finalRecordId = recordId;
    let insertError;

    if (isUpdating && recordId) {
      const { error } = await supabase
        .from('carrier_registrations')
        .update({
          ...carrierData,
          updated_at: new Date().toISOString(),
        })
        .eq('id', recordId)
        .eq('user_id', userId);
      insertError = error;
    } else {
      const { data, error } = await supabase
        .from('carrier_registrations')
        .insert([carrierData])
        .select()
        .single();
      insertError = error;
      if (data) {
        finalRecordId = data.id;
      }
    }

    if (insertError) throw insertError;
    return finalRecordId;
  };

  const submitCarrier = async (
    formData: CarrierFormData,
    isUpdating: boolean = false,
    recordId: string | null = null
  ) => {
    setLoading(true);
    setErrors({});

    try {
      // 1. Verificar autenticación
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('Authentication required');
      }

      const userId = session.data.session.user.id;

      // 2. Sanitizar y validar datos
      const sanitizedData = sanitizeFormData(formData);
      const parsed = carrierSchema.safeParse(sanitizedData);

      if (!parsed.success) {
        const fieldErrors: { [key: string]: string } = {};
        Object.entries(parsed.error.formErrors.fieldErrors).forEach(
          ([field, messages]) => {
            if (messages?.length) {
              fieldErrors[field] = messages[0];
            }
          }
        );
        setErrors(fieldErrors);
        return { success: false, error: 'Please verify the entered data' };
      }

      const validatedData = parsed.data;

      // 3. Verificar si ya existe un carrier con el mismo email o DOT
      if (!isUpdating) {
        const existingCarrier = await checkExistingCarrier(
          validatedData.email,
          validatedData.dotNumber,
          userId
        );

        if (existingCarrier) {
          throw new Error('A carrier with this email or DOT number already exists');
        }
      }

      // 4. Preparar datos para guardar
      const carrierData: any = {
        company_name: validatedData.companyName,
        contact_name: validatedData.contactName,
        email: validatedData.email,
        phone: validatedData.phone,
        dot_number: validatedData.dotNumber,
        equipment_type: validatedData.equipmentType,
        user_id: userId,
      };

      // Agregar campos opcionales de FMCSA/DOT
      if (validatedData.mcNumber) {
        carrierData.mc_number = validatedData.mcNumber;
      }
      if (validatedData.insurancePolicyNumber) {
        carrierData.insurance_policy_number = validatedData.insurancePolicyNumber;
      }
      if (validatedData.insuranceProvider) {
        carrierData.insurance_provider = validatedData.insuranceProvider;
      }
      if (validatedData.insuranceCoverageAmount) {
        // Convertir string a número (en centavos, como otros precios en el sistema)
        const amount = parseFloat(validatedData.insuranceCoverageAmount);
        if (!isNaN(amount) && amount > 0) {
          carrierData.insurance_coverage_amount = Math.round(amount * 100); // Convertir a centavos
        }
      }
      if (validatedData.insuranceExpirationDate) {
        carrierData.insurance_expiration_date = validatedData.insuranceExpirationDate;
      }

      // 5. Guardar carrier
      const finalRecordId = await saveCarrier(carrierData, isUpdating, recordId, userId);

      // 6. Enviar email de confirmación
      try {
        await sendRequestConfirmation({
          email: formData.email,
          name: formData.contactName,
          type: 'carrier',
          isUpdate: isUpdating,
          formData: {
            companyName: formData.companyName,
            contactName: formData.contactName,
            email: formData.email,
            phone: formData.phone,
            dotNumber: formData.dotNumber,
            equipmentType: formData.equipmentType,
          },
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
      }

      // 7. Track successful form submission
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'engagement',
          event_label: 'carrier_form',
          form_type: 'carrier',
        });
      }

      // 8. Retornar éxito (el componente manejará el alert)
      return { success: true, recordId: finalRecordId };
    } catch (err: any) {
      const message = err?.message || 'Failed to submit carrier registration';
      setErrors({ submit: message });
      
      // El componente manejará los alerts, solo retornamos el error
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    errors,
    submitCarrier,
    validateField,
    clearFieldError,
    sanitizeDOT,
    setErrors,
  };
};